import PropTypes from "prop-types"
import { Row, Col, Divider, Flex, Tooltip } from "antd"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import { numberToCurrency } from "helpers/string"
import { spacings } from "themes/variables"
import { useTranslation } from "react-i18next"
import useSummaryDetails from "../../hooks/useSummaryDetails"
import SummaryKeyValue from "../../SummaryKeyValue"

/**
 * Renders a single price-summary snapshot (from price_change_histories / original_price_history)
 * using the SAME layout as the live PaymentSummary: itemized pricing, an Add-ons section, subtotal,
 * discount, a grouped collapsible "Tax & Fees" row, then total / payment / remaining balance.
 *
 * Snapshot add-ons live in `payment_summary.add_ons` (shape: { name, quantity, unit_price,
 * total_amount }) — they have no photo, so the photo thumbnail from the live summary is omitted.
 */
const SnapshotPriceDetails = ({ paymentSummary, paymentLine }) => {
  const { t } = useTranslation()
  const summaryDetails = useSummaryDetails(paymentSummary, [], { combineFees: true, mergeAllFeesToTax: true })
  const { total_price = 0, remaining_balance = 0, add_ons = [], adjust_price_history = [] } = paymentSummary ?? {}

  // Split the details exactly like the live PaymentSummary so the sections line up identically.
  const taxFeesIndex = summaryDetails.findIndex((d) => d?.combinedFee || d?.labelKey === "client.payment.labels.taxes_fees")
  const beforeTaxAll = taxFeesIndex >= 0 ? summaryDetails.slice(0, taxFeesIndex) : summaryDetails
  const taxAndAfterDetails = taxFeesIndex >= 0 ? summaryDetails.slice(taxFeesIndex) : []
  const discountIndex = beforeTaxAll.findIndex((d) => d?.isDiscount)
  const discountDetails = discountIndex >= 0 ? [beforeTaxAll[discountIndex]] : []
  const preDiscountDetails = discountIndex >= 0 ? beforeTaxAll.slice(0, discountIndex) : beforeTaxAll
  const postDiscountDetails = discountIndex >= 0 ? beforeTaxAll.slice(discountIndex + 1) : []
  const subtotalIndex = preDiscountDetails.findIndex((d) => d.isSubtotal)
  const pricingLines = subtotalIndex >= 0 ? preDiscountDetails.slice(0, subtotalIndex) : preDiscountDetails
  const subtotalLine = subtotalIndex >= 0 ? [preDiscountDetails[subtotalIndex]] : []
  const postSubtotalLines = subtotalIndex >= 0 ? preDiscountDetails.slice(subtotalIndex + 1) : []
  const hasAddOns = (add_ons?.length ?? 0) > 0

  return (
    <Flex vertical gap={spacings.XS}>
      <SummaryKeyValue details={pricingLines} valueAlign="right" />
      {hasAddOns && (
        <>
          <Divider size="small" className="m-0" />
          <Text fw={600}>{t("booking.add_ons.title", { defaultValue: "Add-ons" })}</Text>
          {add_ons.map((a, idx) => (
            <Row key={`${a?.name}-${idx}`} align="middle" gutter={8}>
              <Col xs={14} md={12}>
                <Flex vertical>
                  <Text fw={500}>{a?.name}</Text>
                  <Text type="secondary">{`${a?.quantity} × ${numberToCurrency(Number(a?.unit_price))}`}</Text>
                </Flex>
              </Col>
              <Col xs={10} md={12} style={{ textAlign: "right" }}>
                <Text fw={500}>{numberToCurrency(Number(a?.total_amount ?? a?.total_price ?? 0))}</Text>
              </Col>
            </Row>
          ))}
        </>
      )}
      {hasAddOns && <Divider size="small" className="m-0" />}
      {subtotalLine.length > 0 && <SummaryKeyValue details={subtotalLine} valueAlign="right" />}
      {postSubtotalLines.length > 0 && <SummaryKeyValue details={postSubtotalLines} valueAlign="right" />}
      {discountDetails.length > 0 && <SummaryKeyValue details={discountDetails} valueAlign="right" />}
      {postDiscountDetails.length > 0 && <SummaryKeyValue details={postDiscountDetails} valueAlign="right" />}
      {taxAndAfterDetails.length > 0 && <SummaryKeyValue details={taxAndAfterDetails} valueAlign="right" />}
      <Divider size="small" className="m-0" />
      {adjust_price_history?.map((item, idx) => {
        const { price_difference, reason } = item || {}
        return (
          <Row key={(item?.id ?? idx) + "-price-change"}>
            <Col xs={14} md={12}>
              <Flex align="center" gap={4}>
                <Text>Price Change</Text>
                {reason && (
                  <Tooltip title={reason}>
                    <span style={{ display: "inline-flex" }}>
                      <Icon type="feather" name="Info" width={16} height={16} />
                    </span>
                  </Tooltip>
                )}
              </Flex>
            </Col>
            <Col xs={10} md={12} style={{ textAlign: "right" }}>
              <Text fw={500}>
                {price_difference > 0 ? "+" : ""}
                {numberToCurrency(price_difference)}
              </Text>
            </Col>
          </Row>
        )
      })}
      <Row>
        <Col xs={14} md={12}>
          <Text fw={700}>{t("client.client_booking.total")}</Text>
        </Col>
        <Col xs={10} md={12} style={{ textAlign: "right" }}>
          <Text fw={700}>{numberToCurrency(total_price)}</Text>
        </Col>
      </Row>
      {paymentLine?.amount != null && (
        <Row>
          <Col xs={14} md={12}>
            <Text>{paymentLine.label}</Text>
          </Col>
          <Col xs={10} md={12} style={{ textAlign: "right" }}>
            <Text>{numberToCurrency(paymentLine.amount)}</Text>
          </Col>
        </Row>
      )}
      <Row>
        <Col xs={14} md={12}>
          <Text fw={700}>{t("client.payment.labels.remaining_balance")}</Text>
        </Col>
        <Col xs={10} md={12} style={{ textAlign: "right" }}>
          <Text fw={700}>{numberToCurrency(remaining_balance)}</Text>
        </Col>
      </Row>
    </Flex>
  )
}

SnapshotPriceDetails.propTypes = {
  paymentSummary: PropTypes.object,
  paymentLine: PropTypes.shape({
    label: PropTypes.string,
    amount: PropTypes.number
  })
}

export default SnapshotPriceDetails
